import axios from "axios";
import Cookies from "js-cookie";
import { AWS_URL } from "../Constant";

const customerSchemeApi = axios.create({
  baseURL: AWS_URL,
  timeout: 30000,
});

customerSchemeApi.interceptors.request.use((config) => {
  const authData = JSON.parse(sessionStorage.getItem("customer_scheme_auth") || "{}");
  const customerToken = authData?.accessToken;
  const employeeToken = Cookies.get("jwt_token");
  const token = employeeToken || customerToken;

  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  return config;
});

customerSchemeApi.interceptors.response.use(
  (response) => {
    if (response?.data?.success === false) {
      return Promise.reject({ response: { data: response.data } });
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const fetchSchemeList = () => customerSchemeApi.get("/list");

export const fetchComboProducts = () => customerSchemeApi.get("/combo-products");

export const fetchIndividualProducts = () =>
  customerSchemeApi.get("/individual-products");

export const enrollCustomerScheme = (payload) =>
  customerSchemeApi.post("/enroll", payload);

export const redeemEnrollmentProducts = (payload) =>
  customerSchemeApi.post("/enrollment/redeem", payload);

export const fetchEnrollmentRedemptions = (enrollmentId) =>
  customerSchemeApi.get(`/enrollment/${enrollmentId}/redemptions`);

export const updateEnrollmentPaymentPlan = (payload) =>
  customerSchemeApi.post("/enrollment/payment-plan", payload);

export const paySchemeInstallments = (payload) =>
  customerSchemeApi.post("/pay", payload);

export const fetchCustomerEnrollment = (customerId, token) =>
  customerSchemeApi.get(`/enrollment/${customerId}`, token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined);

export const fetchAllEnrollments = () =>
  customerSchemeApi.get("/enrollments");

export const createRazorpayOrder = (payload, token) =>
  customerSchemeApi.post("/payment/razorpay/order", payload, token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined);

export const verifyRazorpayPayment = (payload, token) =>
  customerSchemeApi.post("/payment/razorpay/verify", payload, {
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    timeout: 60000,
  });

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const scriptSelector = 'script[src="https://checkout.razorpay.com/v1/checkout.js"]';
    const existingScript = document.querySelector(scriptSelector);

    if (existingScript) {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () => resolve(false));
      setTimeout(() => resolve(Boolean(window.Razorpay)), 500);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const logRazorpayDebug = () => {};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.message ||
  fallback;

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  return "";
};

const getVerificationErrorMessage = (error) => {
  if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
    return "Bank verification is taking longer than expected. Please wait while we confirm your payment. You may receive an email confirmation shortly.";
  }
  if (error?.response?.status === 504 || error?.response?.status === 502) {
    return "Backend server is busy. Your payment may still be processed. Please check your email for confirmation or contact support.";
  }
  return getErrorMessage(error, "Payment verification failed. Please contact support with your transaction reference.");
};

const getCheckoutFailureMessage = (error = {}) => {
  const reason = error?.reason;
  const description = error?.description || "Razorpay payment failed.";

  if (reason === "payment_risk_check_failed") {
    return "Bank flagged this payment as risky and declined it before processing. Please try another card or UPI, or contact your bank and try again.";
  }

  if (reason === "payment_failed" || reason === "card_declined") {
    return "Payment was declined by the bank. Please try another card or payment method, or contact your bank.";
  }

  return description;
};

export async function paySchemeInstallment({
  token,
  enrollmentId,
  amount,
  units,
  gateway,
  numberOfInstallments,
  installmentNumbers,
  remarks,
  customerName,
  customerPhone,
  customerEmail,
}) {
  const isLoaded = await loadRazorpayScript();
  if (!isLoaded) {
    throw new Error("Razorpay SDK failed to load.");
  }

  if (!enrollmentId) {
    throw new Error("Enrollment ID is required.");
  }

  const orderPayload = {
    enrollmentId,
    remarks: remarks || "Scheme payment",
  };

  if (Array.isArray(installmentNumbers) && installmentNumbers.length > 0) {
    orderPayload.installmentNumbers = installmentNumbers;
  }

  if (numberOfInstallments !== undefined && numberOfInstallments !== null && numberOfInstallments !== "") {
    orderPayload.numberOfInstallments = Number(numberOfInstallments || 1);
  }

  if (amount !== undefined && amount !== null && amount !== "") {
    orderPayload.amount = Number(amount);
  }

  if (gateway) {
    orderPayload.gateway = gateway;
  }

  if (units !== undefined && units !== null && units !== "") {
    orderPayload.units = Number(units);
  }

  logRazorpayDebug("create_order:start", {
    enrollmentId,
    hasInstallmentNumbers: Array.isArray(orderPayload.installmentNumbers),
    numberOfInstallments: orderPayload.numberOfInstallments,
  });

  const orderResponse = await createRazorpayOrder(orderPayload, token);
  const orderData = orderResponse?.data?.data || orderResponse?.data || {};

  logRazorpayDebug("create_order:success", {
    orderId: orderData?.orderId,
    keyId: orderData?.keyId,
    isTestKey: String(orderData?.keyId || "").startsWith("rzp_test_"),
    localPaymentId: orderData?.localPaymentId,
    amount: orderData?.amount,
    currency: orderData?.currency,
  });

  const missingFields = [];
  if (!orderData?.orderId) missingFields.push("orderId");
  if (!orderData?.keyId) missingFields.push("keyId (Razorpay Key ID)");
  if (!orderData?.localPaymentId) missingFields.push("localPaymentId");
  if (!orderData?.amount) missingFields.push("amount");

  if (missingFields.length > 0) {
    throw new Error(
      `Backend order creation failed. Missing: ${missingFields.join(", ")}. ` +
      `Make sure RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET are set on your backend server.`
    );
  }

  return new Promise((resolve, reject) => {
    const contact =
      normalizePhone(customerPhone) ||
      normalizePhone(orderData?.customer?.phone);

    const options = {
      key: orderData.keyId,
      amount: orderData.amount,
      currency: orderData.currency || "INR",
      name: "Garuda Mart",
      description: "Scheme installment payment",
      order_id: orderData.orderId,
      prefill: {
        name: customerName || orderData?.customer?.name || "",
        contact,
        email: customerEmail || orderData?.customer?.email || "",
      },
      notes: {
        enrollmentId: orderData.enrollmentId || enrollmentId,
        localPaymentId: orderData.localPaymentId,
        installmentNumbers: JSON.stringify(orderData.installmentNumbers || installmentNumbers || []),
      },
      theme: {
        color: "#2563eb",
      },
      handler: async (response) => {
        logRazorpayDebug("checkout:handler_called", {
          razorpay_order_id: response?.razorpay_order_id,
          razorpay_payment_id: response?.razorpay_payment_id,
          hasSignature: Boolean(response?.razorpay_signature),
        });

        try {
          const verifyPayload = {
            localPaymentId: orderData.localPaymentId,
            razorpay_order_id: response?.razorpay_order_id,
            razorpay_payment_id: response?.razorpay_payment_id,
            razorpay_signature: response?.razorpay_signature,
            approvedBy: "RAZORPAY_REACT",
          };

          logRazorpayDebug("verify:start", {
            localPaymentId: verifyPayload.localPaymentId,
            razorpay_order_id: verifyPayload.razorpay_order_id,
            razorpay_payment_id: verifyPayload.razorpay_payment_id,
            hasSignature: Boolean(verifyPayload.razorpay_signature),
          });

          const verifyResponse = await verifyRazorpayPayment(verifyPayload, token);
          logRazorpayDebug("verify:success", {
            status: verifyResponse?.status,
            success: verifyResponse?.data?.success,
            message: verifyResponse?.data?.message,
          });
          resolve(verifyResponse?.data || verifyResponse);
        } catch (error) {
          logRazorpayDebug("verify:error", {
            code: error?.code,
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          });
          reject({
            success: false,
            message: getVerificationErrorMessage(error),
            raw: error,
          });
        }
      },
      modal: {
        ondismiss: () => {
          logRazorpayDebug("checkout:modal_dismissed", {
            orderId: orderData.orderId,
            localPaymentId: orderData.localPaymentId,
          });
          reject({
            success: false,
            message: "Payment popup closed by user.",
          });
        },
      },
    };

    // Validate all required fields are non-empty
    if (!options.key || String(options.key).trim() === "") {
      reject({
        success: false,
        message: "Razorpay Key ID is empty. Backend did not configure RAZORPAY_KEY_ID. Contact support.",
      });
      return;
    }
    if (!options.order_id || String(options.order_id).trim() === "") {
      reject({
        success: false,
        message: "Order ID is empty. Backend order creation failed.",
      });
      return;
    }
    if (!Number.isFinite(options.amount) || options.amount <= 0) {
      reject({
        success: false,
        message: `Invalid amount: ${options.amount}. Order amount must be greater than 0.`,
      });
      return;
    }

    try {
      if (typeof window === "undefined" || typeof window.Razorpay !== "function") {
        reject({
          success: false,
          message: "Razorpay SDK is unavailable. Please refresh the page and try again.",
        });
        return;
      }

      const razorpay = new window.Razorpay(options);

      if (typeof razorpay?.on === "function") {
        razorpay.on("payment.failed", (response) => {
          const failureError = response?.error || {};
          logRazorpayDebug("checkout:payment_failed", {
            code: failureError?.code,
            description: failureError?.description,
            reason: failureError?.reason,
            source: failureError?.source,
            step: failureError?.step,
            metadata: failureError?.metadata,
          });
          reject({
            success: false,
            message: getCheckoutFailureMessage(failureError),
            reason: failureError?.reason,
            code: failureError?.code,
            metadata: failureError?.metadata,
            rawError: failureError,
          });
        });
      }

      if (typeof razorpay?.open !== "function") {
        reject({
          success: false,
          message: "Razorpay checkout could not be initialized.",
        });
        return;
      }

      razorpay.open();
    } catch (error) {
      logRazorpayDebug("checkout:init_error", {
        message: error?.message,
        stack: error?.stack,
      });
      reject({
        success: false,
        message: error?.message || "Unable to open Razorpay checkout.",
        raw: error,
      });
    }
  });
}

export const approvePayment = (paymentId, payload = {}) =>
  customerSchemeApi.post("/payment/approve", { paymentId, ...payload });

export const rejectPayment = (paymentId, payload = {}) =>
  customerSchemeApi.post("/payment/reject", { paymentId, ...payload });

export const fetchPayments = (params = {}) =>
  customerSchemeApi.get("/payments", { params });

export const fetchPendingPayments = () =>
  customerSchemeApi.get("/payments", { params: { status: "PENDING" } });

export const triggerPaymentReminders = (payload = {}) =>
  customerSchemeApi.post("/reminders/trigger", payload);

export default customerSchemeApi;
